// adminService.js — ARQUIVO COMPLETO
import { api } from "../config/api";

const adminService = {
    // ─── Dashboard ──────────────────────────────────────────────────────
    getStats: async () => {
        const response = await api.get("/admin/dashboard/stats");
        return response.data;
    },

    getActivities: async (take = 20) => {
        const response = await api.get("/admin/dashboard/activities", {
            params: { take }
        });
        return response.data;
    },

    // ─── Chat de Suporte ──────────────────────────────────────────────
    getConversations: async (filter = "todos", search = "") => {
        const response = await api.get("/admin/support/conversations", {
            params: { filter, search }
        });
        return response.data;
    },

    getMessages: async (conversationId) => {
        try {
            const response = await api.get(`/admin/support/messages/${conversationId}`);
            return response.data;
        } catch (error) {
            console.error('Erro ao carregar mensagens:', error);
            throw error;
        }
    },

    sendMessage: async (conversationId, message) => {
        const response = await api.post("/admin/support/messages", {
            conversationId,
            message
        });
        return response.data;
    },

    resolveConversation: async (conversationId) => {
        const response = await api.patch(`/admin/support/conversations/${conversationId}/resolve`);
        return response.data;
    },

    reopenConversation: async (conversationId) => {
        const response = await api.patch(`/admin/support/conversations/${conversationId}/reopen`);
        return response.data;
    },

    // ─── Usuários ──────────────────────────────────────────────────────
    getCompanies: async (filter = "todos", search = "") => {
        const response = await api.get("/admin/users/companies", {
            params: { filter, search }
        });
        return response.data;
    },

    getFreelancers: async (filter = "todos", search = "") => {
        const response = await api.get("/admin/users/freelancers", {
            params: { filter, search }
        });
        return response.data;
    },

    updateGold: async (id, enable, expiresAt = null) => {
        const response = await api.put(`/admin/users/${id}/gold`, {
            enable,
            expiresAt
        });
        return response.data;
    },

    setBan: async (id, enable, expiresAt = null) => {
        const response = await api.put(`/admin/users/${id}/ban`, {
            enable,
            expiresAt
        });
        return response.data;
    },

    deleteUser: async (id) => {
        const response = await api.delete(`/admin/users/${id}`);
        return response.data;
    },

    updateUserDetails: async (id, data) => {
        const response = await api.put(`/admin/users/${id}/details`, data);
        return response.data;
    },

    // ─── Wrappers usados pelo AdminUsers.jsx (empresas e freelancers) ──
    updateCompanyPlan: async (id, plan, expiresAt = null) => {
        return adminService.updateGold(id, plan !== "FREE", expiresAt);
    },

    updateFreelancerPlan: async (id, plan, expiresAt = null) => {
        return adminService.updateGold(id, plan !== "FREE", expiresAt);
    },

    toggleBlockCompany: async (id, enable) => {
        return adminService.setBan(id, enable);
    },

    toggleBlockFreelancer: async (id, enable) => {
        return adminService.setBan(id, enable);
    },

    deleteCompany: async (id) => {
        return adminService.deleteUser(id);
    },

    deleteFreelancer: async (id) => {
        return adminService.deleteUser(id);
    },

    // ─── Vagas ─────────────────────────────────────────────────────────
    getJobs: async (status = "abertas") => {
        const response = await api.get("/admin/jobs", {
            params: { status }
        });
        return response.data;
    },

    deleteJob: async (id) => {
        const response = await api.delete(`/admin/jobs/${id}`);
        return response.data;
    },

    // ─── Avaliações ────────────────────────────────────────────────────
    getEvaluations: async () => {
        const response = await api.get("/admin/evaluations");
        return response.data;
    },

    // ─── Financeiro ────────────────────────────────────────────────────
    getWalletTransactions: async (params = {}) => {
        const response = await api.get("/admin/wallets/transactions", { params });
        return response.data;
    },

    getWalletBalances: async (params = {}) => {
        const response = await api.get("/admin/wallets/balances", { params });
        return response.data;
    },

    getWalletsSummary: async () => {
        const response = await api.get("/admin/wallets/summary");
        return response.data;
    },

    getStuckReservations: async () => {
        const response = await api.get("/admin/wallets/stuck-reservations");
        return response.data;
    },

    releaseStuckReservations: async () => {
        const response = await api.post("/admin/wallets/stuck-reservations/release");
        return response.data;
    },

    // ─── Taxas e Tarifas ───────────────────────────────────────────────
    getFees: async () => {
        const response = await api.get("/admin/settings/fees");
        return response.data;
    },

    updateFees: async (fees) => {
        const response = await api.put("/admin/settings/fees", fees);
        return response.data;
    },

    getPlatformAccount: async () => {
        const response = await api.get("/admin/settings/platform-account");
        return response.data;
    },

    selectPlatformAccount: async (userId) => {
        const response = await api.put("/admin/settings/platform-account", { userId });
        return response.data;
    },

    cancelPlatformAccount: async () => {
        const response = await api.delete("/admin/settings/platform-account");
        return response.data;
    },

    // ─── Planos Gold ─────────────────────────────────────────────────
    getGoldPlans: async () => {
        const response = await api.get("/admin/settings/gold-plans");
        return response.data;
    },

    updateGoldPlans: async (plans) => {
        const response = await api.put("/admin/settings/gold-plans", plans);
        return response.data;
    },

    // ─── Plano Premium ───────────────────────────────────────────────
    getPremiumPlan: async () => {
        const response = await api.get("/admin/settings/premium-plan");
        return response.data;
    },

    updatePremiumPlan: async (premium) => {
        const response = await api.put("/admin/settings/premium-plan", premium);
        return response.data;
    },

    // ─── Documentos ──────────────────────────────────────────────────
    getDocuments: async (tab = "pendentes") => {
        const response = await api.get("/admin/documents", { params: { tab } });
        return response.data;
    },

    approveDocument: async (userId) => {
        const response = await api.put(`/admin/documents/${userId}/approve`);
        return response.data;
    },

    rejectDocument: async (userId, reason) => {
        const response = await api.put(`/admin/documents/${userId}/reject`, { reason });
        return response.data;
    },

    // ─── Treinamento ─────────────────────────────────────────────────
    getTrainingVideos: async () => {
        const response = await api.get("/admin/training-videos");
        return response.data;
    },

    createTrainingVideo: async (data) => {
        const response = await api.post("/admin/training-videos", data);
        return response.data;
    },

    deleteTrainingVideo: async (id) => {
        const response = await api.delete(`/admin/training-videos/${id}`);
        return response.data;
    },

    // ─── Contratos Corporativos ───────────────────────────────────────
    getCorporateContracts: async () => {
        const response = await api.get("/admin/corporate-contracts/all-contracts");
        return response.data;
    },

    createCorporateContract: async (data) => {
        const response = await api.post("/admin/corporate-contracts/create-contract", data);
        return response.data;
    },

    getCorporateContractCompanies: async () => {
        const response = await api.get("/admin/corporate-contracts/companies");
        return response.data;
    },

    getCorporateContractById: async (id) => {
        const response = await api.get(`/admin/corporate-contracts/${id}`);
        return response.data;
    },

    updateCorporateContract: async (id, data) => {
        const response = await api.put(`/admin/corporate-contracts/${id}`, data);
        return response.data;
    },

    deleteCorporateContract: async (id) => {
        const response = await api.delete(`/admin/corporate-contracts/${id}`);
        return response.data;
    },

    activateLaunchGold: async () => {
        const response = await api.post("/admin/subscriptions/activate-launch-gold?confirm=true");
        return response.data;
    },
    activateGoldForAllFreelancers: async () => {
        const response = await api.post("/admin/subscriptions/activate-gold-all-freelancers?confirm=true");
        return response.data;
    },

    deactivateGoldForAllFreelancers: async () => {
        const response = await api.post("/admin/subscriptions/deactivate-gold-all-freelancers?confirm=true");
        return response.data;
    },

    getPlatformWallet: async () => {
        const response = await api.get("/admin/settings/platform-wallet");
        return response.data;
    },

    sendLaunchConfirmationEmails: async () => {
        const response = await api.post("/admin/launch-confirmation/send");
        return response.data;
    },

    // ─── Central de Notificações ───────────────────────────────────────
    getNotificationOverview: async () => {
        const response = await api.get("/admin/notifications/overview");
        return response.data;
    },

    getNotifications: async (status = "todas") => {
        const response = await api.get("/admin/notifications", { params: { status } });
        return response.data;
    },

    getNotificationDetail: async (id) => {
        const response = await api.get(`/admin/notifications/${id}`);
        return response.data;
    },

    createNotification: async (dto) => {
        const response = await api.post("/admin/notifications", dto);
        return response.data;
    },

    updateNotification: async (id, dto) => {
        const response = await api.put(`/admin/notifications/${id}`, dto);
        return response.data;
    },

    sendTestNotification: async (id, testUserId = null) => {
        const response = await api.post(`/admin/notifications/${id}/send-test`, { testUserId });
        return response.data;
    },

    cancelNotification: async (id) => {
        const response = await api.patch(`/admin/notifications/${id}/cancel`);
        return response.data;
    },

    duplicateNotification: async (id) => {
        const response = await api.post(`/admin/notifications/${id}/duplicate`);
        return response.data;
    },

    deleteNotification: async (id) => {
        const response = await api.delete(`/admin/notifications/${id}`);
        return response.data;
    },

    uploadNotificationImage: async (file) => {
        const formData = new FormData();
        formData.append("file", file);
        const response = await api.post("/admin/notifications/upload-image", formData, {
            headers: { "Content-Type": "multipart/form-data" }
        });
        return response.data;
    },

    getAdmins: async (filter = "todos", search = "") => {
        const response = await api.get("/admin/users/admins", {
            params: { filter, search }
        });
        return response.data;
    },

    resendNotification: async (id) => {
        const response = await api.post(`/admin/notifications/${id}/resend`);
        return response.data;
    },
    getAttributions: (search) =>
        api.get("/admin/attributions", { params: { search } }).then(r => r.data),

    getAttributionFreelancers: (name) =>
        api.get(`/admin/attributions/${encodeURIComponent(name)}/freelancers`).then(r => r.data),
    // ─── Stubs temporários (ainda não implementados no backend) ────────
    getPendingDeposits: async () => [],
    getPendingPayouts: async () => [],
    getPayoutHistory: async () => [],
    approveDeposit: async () => { },
    rejectDeposit: async () => { },
    approvePayout: async () => { },
    rejectPayout: async () => { },
};

export default adminService;